/**
 * ContactLensSelector — Web component for eyewa-style Left/Right eye power picker.
 *
 * Replaces Dawn's standard variant picker for contact lens products
 * (products with a single option named "Power").
 *
 * COLOR CL MODE: When data-color-mode="true", shows color swatches and fetches
 * per-color power products via AJAX. Parent product has Color variants,
 * children have Power variants linked via custom.linked_colors metafield.
 *
 * Adds two separate line items to cart via Shopify AJAX Cart API,
 * each tagged with line-item properties for eye identification.
 *
 * @extends HTMLElement
 */
if (!customElements.get('contact-lens-selector')) {
  customElements.define(
    'contact-lens-selector',
    class ContactLensSelector extends HTMLElement {
      constructor() {
        super();

        this.variants = [];
        this.hasPlano = false;
        this.planoValue = '';
        this.mode = 'no-power';

        // Color CL state
        this.isColorCL = false;
        this.colors = [];
        this.selectedColor = null;
        this.selectedColorVariantId = null;
        this.colorCache = {}; // { handle: { variants: [...], ... } }
        this.currentPowerProduct = null; // fetched child product data

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
      }

      connectedCallback() {
        this.parseVariantData();
        this.cacheDOM();
        this.bindEvents();

        if (this.isColorCL) {
          this.initColorMode();
        } else {
          this.markSoldOutOptions();
          this.syncSameRx();
          if (!this.hasPlano) {
            this.activateWithPower();
          }
        }
        this._updateSkuDisplay();
        this._updatePackVisibility();
      }

      /* ── Data ── */

      parseVariantData() {
        const dataEl = this.querySelector('[data-cls-variants-json]');
        if (!dataEl) return;

        try {
          const data = JSON.parse(dataEl.textContent);
          this.variants = data.variants || [];
          this.hasPlano = data.hasPlano || false;
          this.planoValue = data.planoValue || '';
          this.translations = data.translations || {};
          this.isColorCL = data.isColorCL || false;
          this.colors = data.colors || [];
          this.linkedPower = data.linkedPower || [];
          this.sectionId = data.sectionId || '';
        } catch (e) {
          console.error('ContactLensSelector: Failed to parse variant data', e);
        }
      }

      /* ── DOM refs ── */

      cacheDOM() {
        this.pillNoPower = this.querySelector('[data-cls-pill="no-power"]');
        this.pillWithPower = this.querySelector('[data-cls-pill="with-power"]');

        // Color CL: self-contained color swatches
        this.colorSwatches = this.querySelectorAll('[data-cls-color]');
        this.colorNameDisplay = this.querySelector('[data-cls-color-name]');
        this.prescriptionPanel = this.querySelector('[data-cls-prescription]');
        this.sameRxCheckbox = this.querySelector('[data-cls-same-rx]');
        this.sameRxWrap = this.querySelector('[data-cls-same-rx-wrap]');
        this.eyeTable = this.querySelector('[data-cls-eye-table]');

        this.sphereLeft = this.querySelector('[data-cls-sphere][data-eye="left"]');
        this.sphereRight = this.querySelector('[data-cls-sphere][data-eye="right"]');
        this.boxesLeft = this.querySelector('[data-cls-boxes][data-eye="left"]');
        this.boxesRight = this.querySelector('[data-cls-boxes][data-eye="right"]');
        this.rightEyeColumn = this.querySelector('.cls-eye--right');

        this.packSelect = this.querySelector('[data-cls-pack]');
        this.packWrap = this.querySelector('[data-cls-pack-wrap]');
        this.addBtn = this.querySelector('[data-cls-add-to-cart]');
        this.btnText = this.querySelector('[data-cls-btn-text]');
        this.spinner = this.querySelector('[data-cls-spinner]');
        this.errorBox = this.querySelector('[data-cls-error]');

        // Color CL elements
        this.powerLoading = this.querySelector('[data-cls-power-loading]');

        // Canonical product price block (Dawn '#price-{section}'). Color CL has
        // no native Dawn variant change, so updatePrice() writes to it directly.
        const mainPriceBlock = this.sectionId
          ? document.getElementById('price-' + this.sectionId)
          : null;
        this.mainPriceEl = mainPriceBlock
          ? mainPriceBlock.querySelector('.price')
          : null;
      }

      /* ── Events ── */

      bindEvents() {
        if (this.pillNoPower) {
          this.pillNoPower.addEventListener('click', () => this.activateNoPower());
        }
        if (this.pillWithPower) {
          this.pillWithPower.addEventListener('click', () => this.activateWithPower());
        }
        if (this.sameRxCheckbox) {
          this.sameRxCheckbox.addEventListener('change', () => {
            this.syncSameRx();
            this.updateButtonState();
            this._updateSkuDisplay();
          });
        }
        if (this.sphereLeft) {
          this.sphereLeft.addEventListener('change', () => {
            if (this.sameRxCheckbox && this.sameRxCheckbox.checked) {
              this.sphereRight.value = this.sphereLeft.value;
            }
            this.updateButtonState();
            this._updateSkuDisplay();
          });
        }
        if (this.boxesLeft) {
          this.boxesLeft.addEventListener('change', () => {
            if (this.sameRxCheckbox && this.sameRxCheckbox.checked) {
              this.boxesRight.value = this.boxesLeft.value;
            }
          });
        }
        if (this.sphereRight) {
          this.sphereRight.addEventListener('change', () => {
            this.updateButtonState();
            this._updateSkuDisplay();
          });
        }
        if (this.addBtn) {
          this.addBtn.addEventListener('click', () => this.handleAddToCart());
        }

        // Color CL: bind swatch click handlers
        if (this.isColorCL && this.colorSwatches.length) {
          this.colorSwatches.forEach((swatch) => {
            swatch.addEventListener('click', () => {
              if (swatch.disabled) return;
              this.selectColor(swatch.dataset.clsColor);
            });
          });
        }
      }

      /* ══════════════════════════════════════════ */
      /* ── COLOR CL MODE                       ── */
      /* ══════════════════════════════════════════ */

      initColorMode() {
        this.syncSameRx();

        // Fetch English variant names from .js endpoint (translation-safe slugs)
        this._fetchEnglishSlugs();

        // Select first available color
        if (this.colors.length > 0) {
          const first = this.colors.find((c) => c.available) || this.colors[0];
          this.selectedColor = first.name;
          this.selectedColorVariantId = first.variantId;
        }
      }

      _fetchEnglishSlugs() {
        // The .js endpoint always returns English variant names regardless of Transcy
        const handle = window.location.pathname.split('/products/')[1]?.split('?')[0];
        if (!handle) return;

        fetch('/products/' + handle + '.js')
          .then((r) => r.ok ? r.json() : null)
          .then((product) => {
            if (!product || !product.variants) return;
            // Map variant ID → English slug
            product.variants.forEach((v) => {
              const colorInfo = this.colors.find((c) => c.variantId === v.id);
              if (colorInfo) {
                colorInfo.slug = (v.option1 || v.title || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
              }
            });
          })
          .catch(() => {});
      }

      /* ── Color selection (self-contained) ── */

      // Find linked power product — slug-based matching (translation-safe)
      _findLinkedProduct(colorName, variantId) {
        if (!this.linkedPower || !this.linkedPower.length) return null;

        const colorInfo = this.colors.find((c) => c.variantId === variantId) ||
                          this.colors.find((c) => c.name === colorName);
        const colorSlug = colorInfo ? colorInfo.slug : '';

        // Match: child product handle contains the color slug
        // e.g., slug "ash-brown" found in handle "bella-one-day-color-ash-brown-with-power"
        if (colorSlug) {
          const match = this.linkedPower.find((lp) => lp.handle && lp.handle.includes(colorSlug));
          if (match) return match;
        }

        return null;
      }

      selectColor(colorName) {
        const colorInfo = this.colors.find((c) => c.name === colorName);
        if (!colorInfo) return;

        this.selectedColor = colorInfo.name;
        this.selectedColorVariantId = colorInfo.variantId;

        // Update active swatch
        this.colorSwatches.forEach((s) => {
          s.classList.toggle('cls-color-swatch--active', s.dataset.clsColor === colorName);
        });

        // Update color name display
        if (this.colorNameDisplay) {
          this.colorNameDisplay.textContent = colorName;
        }

        // Sync gallery image
        const swatch = this.querySelector('[data-cls-color="' + CSS.escape(colorName) + '"]');
        if (swatch) {
          const mediaId = swatch.dataset.mediaId;
          if (mediaId && !mediaId.endsWith('-0')) {
            this._syncGalleryImage(mediaId);
          }
        }

        // Handle power mode
        if (this.mode === 'with-power') {
          const linked = this._findLinkedProduct(colorName, colorInfo.variantId);
          if (linked && linked.handle) {
            this.fetchAndApplyPowerProduct(linked.handle, linked);
          } else {
            this.showError(this.translations.powerNotAvailable || 'Power options are not available for this color.');
          }
        } else {
          this.updatePrice(colorInfo.price, colorInfo.compareAtPrice);
        }

        this.updateButtonState();
        this._updateSkuDisplay();
      }

      _syncGalleryImage(mediaId) {
        // Use Dawn's MediaGallery.setActiveMedia() — instant switch with thumbnail sync
        const section = this.closest('.shopify-section') || document;
        const gallery = section.querySelector('media-gallery');
        if (gallery && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(mediaId, false);
          return;
        }

        // Fallback: direct DOM manipulation
        const mediaItem = section.querySelector('[data-media-id="' + mediaId + '"]');
        if (mediaItem) {
          const siblings = mediaItem.parentElement.querySelectorAll('[data-media-id]');
          siblings.forEach((el) => el.classList.remove('is-active'));
          mediaItem.classList.add('is-active');
        }
      }

      async fetchAndApplyPowerProduct(handle, linkedInfo) {
        // Check cache first
        if (this.colorCache[handle]) {
          this.applyChildVariants(this.colorCache[handle]);
          return;
        }

        // Show loading
        this.showPowerLoading(true);

        try {
          // Use .js endpoint (not .json) — it includes `available` and prices in cents
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const product = await res.json();

          // .js endpoint returns prices in cents and includes `available`, but
          // OMITS inventory_quantity/policy. Merge the real per-power counts that
          // Liquid rendered server-side (linkedInfo.inv, keyed by power/option1).
          // Missing entries → leave undefined so getAvailableStock trusts `available`.
          const invMap = (linkedInfo && linkedInfo.inv) || {};
          const variants = product.variants.map((v) => {
            const inv = invMap[v.option1];
            return {
              id: v.id,
              title: v.title,
              option1: v.option1,
              price: v.price, // already in cents from .js endpoint
              available: v.available,
              inventory_quantity: inv && typeof inv.q === 'number' ? inv.q : v.inventory_quantity,
              inventory_management: inv ? (inv.mgmt ? 'shopify' : null) : v.inventory_management,
              inventory_policy: inv ? inv.pol : v.inventory_policy,
              sku: v.sku || '',
            };
          });

          const childData = {
            productId: product.id,
            handle: handle,
            title: product.title,
            variants: variants,
            price: linkedInfo ? linkedInfo.price : (product.variants[0] ? product.variants[0].price : 0),
            compareAtPrice: linkedInfo ? linkedInfo.compareAtPrice : 0,
          };

          this.colorCache[handle] = childData;
          this.applyChildVariants(childData);
        } catch (err) {
          console.error('ContactLensSelector: Failed to fetch color product', err);
          this.showError(this.translations.error || 'Something went wrong. Please try again.');
        } finally {
          this.showPowerLoading(false);
        }
      }

      applyChildVariants(childData) {
        // Swap variants
        this.variants = childData.variants;
        this.currentPowerProduct = childData;

        // Update price display for with-power
        if (childData.price) {
          this.updatePrice(childData.price, childData.compareAtPrice);
        }

        // Rebuild sphere dropdown options
        this.rebuildSphereOptions();
        this.markSoldOutOptions();
        this.updateButtonState();
        this._updateSkuDisplay();
      }

      rebuildSphereOptions() {
        [this.sphereLeft, this.sphereRight].forEach((select) => {
          if (!select) return;

          // Remember current selection
          const currentVal = select.value;

          // Clear all options except the placeholder
          while (select.options.length > 1) {
            select.remove(1);
          }

          // Add new options from variants
          this.variants.forEach((v) => {
            // Skip plano values
            const vDown = (v.option1 || '').toLowerCase().trim();
            if (vDown === '0.00' || vDown === 'plano' || vDown === '0' || vDown === '+0.00' || vDown === '-0.00') {
              return;
            }

            const opt = document.createElement('option');
            opt.value = v.option1;
            opt.textContent = v.option1;
            select.appendChild(opt);
          });

          // Restore selection if the value still exists
          if (currentVal) {
            const exists = [...select.options].some((o) => o.value === currentVal);
            if (exists) {
              select.value = currentVal;
            } else {
              select.selectedIndex = 0; // reset to placeholder
            }
          }
        });
      }

      showPowerLoading(on) {
        if (this.powerLoading) this.powerLoading.style.display = on ? '' : 'none';
        if (this.sameRxWrap) this.sameRxWrap.style.display = on ? 'none' : '';
        if (this.eyeTable) this.eyeTable.style.display = on ? 'none' : '';
      }

      updatePrice(priceCents, compareAtCents) {
        const priceEl = this.mainPriceEl;
        if (!priceEl) return;

        const onSale = !!(compareAtCents && compareAtCents > priceCents);
        const formatted = this._formatCents(priceCents);

        // Current price — both the regular and sale spans (Dawn shows one or the
        // other via the .price--on-sale class).
        priceEl
          .querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale')
          .forEach((el) => {
            el.textContent = formatted;
          });

        // Struck-through compare-at price inside the sale block. Create it if the
        // initial server render didn't include one (product loaded not-on-sale).
        const saleWrap = priceEl.querySelector('.price__sale');
        if (saleWrap) {
          let compareEl = saleWrap.querySelector('s.price-item--regular');
          if (onSale) {
            if (!compareEl) {
              const span = document.createElement('span');
              compareEl = document.createElement('s');
              compareEl.className = 'price-item price-item--regular';
              span.appendChild(compareEl);
              saleWrap.insertBefore(span, saleWrap.firstChild);
            }
            compareEl.textContent = this._formatCents(compareAtCents);
          } else if (compareEl) {
            compareEl.textContent = '';
          }
        }

        priceEl.classList.toggle('price--on-sale', onSale);
      }

      // Image switching handled by Dawn's variant picker natively

      /* ── Mode toggles ── */

      activateNoPower() {
        this.mode = 'no-power';
        if (this.pillNoPower) this.pillNoPower.classList.add('cls-pill--active');
        if (this.pillWithPower) this.pillWithPower.classList.remove('cls-pill--active');
        if (this.prescriptionPanel) this.prescriptionPanel.style.display = 'none';

        // Show all swatches and restore parent price
        if (this.isColorCL) {
          this.colorSwatches.forEach((s) => s.classList.remove('cls-color-swatch--hidden'));
          if (this.selectedColor) {
            const colorInfo = this.colors.find((c) => c.name === this.selectedColor);
            if (colorInfo) this.updatePrice(colorInfo.price, colorInfo.compareAtPrice);
          }
        }

        this.updateButtonState();
        this._updateSkuDisplay();
        this._updatePackVisibility();
      }

      activateWithPower() {
        this.mode = 'with-power';
        if (this.pillWithPower) this.pillWithPower.classList.add('cls-pill--active');
        if (this.pillNoPower) this.pillNoPower.classList.remove('cls-pill--active');
        if (this.prescriptionPanel) this.prescriptionPanel.style.display = '';

        // In color CL mode, hide colors without power and fetch for current color
        if (this.isColorCL) {
          // Hide swatches that don't have a linked power product (handle-based check)
          this.colorSwatches.forEach((s) => {
            const colorInfo = this.colors.find((c) => String(c.variantId) === s.dataset.variantId);
            const hasPower = colorInfo && colorInfo.slug && this.linkedPower.some((lp) => lp.handle && lp.handle.includes(colorInfo.slug));
            s.classList.toggle('cls-color-swatch--hidden', !hasPower);
          });

          // If current color has no power, auto-select first that does
          if (this.selectedColor) {
            const linked = this._findLinkedProduct(this.selectedColor, this.selectedColorVariantId);
            if (linked && linked.handle) {
              this.fetchAndApplyPowerProduct(linked.handle, linked);
            } else {
              // Auto-select first color with power
              const firstWithPower = this.colors.find((c) => c.slug && this.linkedPower.some((lp) => lp.handle && lp.handle.includes(c.slug)));
              if (firstWithPower) {
                this.selectColor(firstWithPower.name);
                return; // selectColor will call updateButtonState
              }
            }
          }
        }

        this.updateButtonState();
        this._updateSkuDisplay();
        this._updatePackVisibility();
      }

      /* ── Same-Rx sync ── */

      syncSameRx() {
        if (!this.sameRxCheckbox) return;
        const isOn = this.sameRxCheckbox.checked;

        // Hide per-eye box selectors in Same Rx mode (Pack Selection controls quantity)
        const boxFields = this.querySelectorAll('[data-cls-boxes]');
        boxFields.forEach((sel) => {
          const field = sel.closest('.cls-field');
          if (field) field.style.display = isOn ? 'none' : '';
        });

        if (isOn) {
          if (this.sphereLeft && this.sphereRight) {
            this.sphereRight.value = this.sphereLeft.value;
          }
          if (this.rightEyeColumn) this.rightEyeColumn.classList.add('cls-eye--disabled');
          if (this.sphereRight) this.sphereRight.disabled = true;
          if (this.boxesRight) this.boxesRight.disabled = true;
        } else {
          if (this.rightEyeColumn) this.rightEyeColumn.classList.remove('cls-eye--disabled');
          if (this.sphereRight) this.sphereRight.disabled = false;
          if (this.boxesRight) this.boxesRight.disabled = false;
        }

        this._updatePackVisibility();
      }

      /* ── Pack (No. of Boxes) visibility ── */

      _updatePackVisibility() {
        if (!this.packWrap) return;
        // Show when: No-Power mode, OR With-Power + Same Rx ON.
        // Hide when: With-Power + Same Rx OFF (per-eye box selectors handle quantity).
        const sameRxOn = this.sameRxCheckbox && this.sameRxCheckbox.checked;
        const shouldShow = this.mode === 'no-power' || sameRxOn;
        this.packWrap.style.display = shouldShow ? '' : 'none';
      }

      /* ── Variant lookup ── */

      findVariant(power) {
        return this.variants.find((v) => v.option1 === power) || null;
      }

      getAvailableStock(variant) {
        if (!variant || !variant.available) return 0;
        // Untracked inventory → unlimited
        if (!variant.inventory_management) return Infinity;
        // Overselling allowed ("continue selling when out of stock") → unlimited.
        // Respects the merchant's Shopify setting; these never fail at checkout.
        if (variant.inventory_policy === 'continue') return Infinity;
        // Tracked, but distinguish "explicitly 0" from "data missing":
        //   - inventory_quantity === number (0 or positive): use it as hard cap (no overselling)
        //   - inventory_quantity === undefined/null: data source didn't include it (e.g. color CL
        //     child products fetched via /products/{handle}.js, where Shopify's storefront JSON
        //     omits inventory data by design). Trust variant.available — Shopify computed it
        //     server-side from actual stock + policy.
        const q = variant.inventory_quantity;
        if (typeof q !== 'number') return Infinity;
        // q > 0 → hard cap. q <= 0 here is contradictory (we already know the
        // variant is available, which for a "deny" item proves stock >= 1), so the
        // count is stale (e.g. restock after page load). Trust `available` and
        // don't block an item Shopify says is buyable — checkout is the backstop.
        return q > 0 ? q : Infinity;
      }

      /* ── SKU display ── */

      _updateSkuDisplay() {
        const el = document.querySelector('.product__sku');
        const tabCell = document.querySelector('[data-product-tabs-sku]');
        if (!el && !tabCell) return;

        let sku = '';

        if (this.mode === 'no-power') {
          if (this.isColorCL) {
            const color = this.colors.find((c) => c.variantId === this.selectedColorVariantId);
            sku = color ? (color.sku || '') : '';
          } else {
            const plano = this.findVariant(this.planoValue);
            sku = plano ? (plano.sku || '') : '';
          }
        } else {
          // With-power mode
          const isSameRx = this.sameRxCheckbox && this.sameRxCheckbox.checked;
          const leftPower = this.sphereLeft ? this.sphereLeft.value : '';
          const rightPower = isSameRx ? leftPower : (this.sphereRight ? this.sphereRight.value : '');
          const leftVariant = leftPower ? this.findVariant(leftPower) : null;
          const rightVariant = rightPower ? this.findVariant(rightPower) : null;
          const leftSku = leftVariant ? (leftVariant.sku || '') : '';
          const rightSku = rightVariant ? (rightVariant.sku || '') : '';

          if (isSameRx || !rightSku || leftSku === rightSku) {
            sku = leftSku;
          } else if (!leftSku) {
            sku = rightSku;
          } else {
            sku = 'L: ' + leftSku + ' / R: ' + rightSku;
          }
        }

        // Update the .product__sku block (preserve visually-hidden label)
        if (el) {
          const label = el.querySelector('.visually-hidden');
          if (label) {
            el.innerHTML = '';
            el.appendChild(label);
            el.appendChild(document.createTextNode(sku));
          } else {
            el.textContent = sku;
          }
          if (sku) el.classList.remove('visibility-hidden');
          else el.classList.add('visibility-hidden');
        }

        // Update the Additional Information tab cell
        if (tabCell) {
          tabCell.textContent = sku;
        }
      }

      /* ── Button state ── */

      updateButtonState() {
        this.hideError();

        // Respect near-expiry consent block
        if (this.addBtn.dataset.nearExpiryBlocked === 'true') {
          this.addBtn.disabled = true;
          return;
        }

        if (this.mode === 'no-power') {
          if (this.isColorCL) {
            // Color CL no-power: check that selected color variant is available
            const selectedColorData = this.colors.find((c) => c.variantId === this.selectedColorVariantId);
            if (!this.selectedColorVariantId || (selectedColorData && !selectedColorData.available)) {
              this.addBtn.disabled = true;
              this.btnText.textContent = this.addBtn.dataset.soldOutText || 'Sold Out';
              return;
            }
            this.addBtn.disabled = false;
          } else {
            this.addBtn.disabled = false;
          }
          this.btnText.textContent = this.addBtn.dataset.addText || 'Add to Cart';
          return;
        }

        // With-power mode
        if (this.isColorCL && (!this.variants || this.variants.length === 0)) {
          // Still loading or no power product for this color
          this.addBtn.disabled = true;
          this.btnText.textContent = this.addBtn.dataset.selectText || 'Select Power';
          return;
        }

        const leftVal = this.sphereLeft ? this.sphereLeft.value : '';
        const rightVal =
          this.sameRxCheckbox && this.sameRxCheckbox.checked
            ? leftVal
            : this.sphereRight
              ? this.sphereRight.value
              : '';

        if (!leftVal || !rightVal) {
          this.addBtn.disabled = true;
          this.btnText.textContent = this.addBtn.dataset.selectText || 'Select Power';
          return;
        }

        const leftVariant = this.findVariant(leftVal);
        const rightVariant = this.findVariant(rightVal);

        if (!leftVariant || !leftVariant.available || !rightVariant || !rightVariant.available) {
          this.addBtn.disabled = true;
          this.btnText.textContent = this.addBtn.dataset.soldOutText || 'Sold Out';
        } else {
          this.addBtn.disabled = false;
          this.btnText.textContent = this.addBtn.dataset.addText || 'Add to Cart';
        }
      }

      /* ── Error display ── */

      showError(msg) {
        if (!this.errorBox) return;
        this.errorBox.textContent = msg;
        this.errorBox.style.display = '';
      }

      hideError() {
        if (!this.errorBox) return;
        this.errorBox.textContent = '';
        this.errorBox.style.display = 'none';
      }

      /* ── Loading state ── */

      setLoading(on) {
        if (on) {
          this.addBtn.classList.add('cls-add-to-cart--loading');
          this.addBtn.disabled = true;
          this.btnText.style.display = 'none';
          if (this.spinner) this.spinner.style.display = '';
        } else {
          this.addBtn.classList.remove('cls-add-to-cart--loading');
          this.btnText.style.display = '';
          if (this.spinner) this.spinner.style.display = 'none';
          this.updateButtonState();
        }
      }

      /* ── Add to Cart ── */

      handleAddToCart() {
        this.hideError();
        this._addedItems = [];

        const items = this.buildCartItems();
        if (!items) return;

        this.setLoading(true);
        this._addQueue = items;
        this._addIndex = 0;
        this._addNext();
      }

      _addNext() {
        if (this._addIndex >= this._addQueue.length) {
          this.setLoading(false);
          this.showSuccess();
          this._showCartConfirmation();
          return;
        }

        const item = this._addQueue[this._addIndex];

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: item.id, quantity: item.quantity, properties: item.properties || {} }),
        })
          .then((res) => {
            if (!res.ok) throw new Error(res.statusText || 'Cart add failed');
            return res.json();
          })
          .then((response) => {
            if (response.status && response.status >= 400) {
              throw new Error(response.description || response.message || 'Could not add to cart');
            }
            this._addedItems.push(response);
            this._addIndex++;
            this._addNext();
          })
          .catch((err) => {
            this.setLoading(false);
            const msg = err.message && err.message !== 'Could not add to cart'
              ? err.message
              : (this.translations.error || 'This item is sold out or unavailable and cannot be added to cart.');
            this.showError(msg);
          });
      }

      /* ── Custom Cart Confirmation Modal ── */

      _showCartConfirmation() {
        const addedItems = this._addedItems || [];
        this._addedItems = [];

        // Update cart icon badge
        this._updateCartBadge();

        // Build modal content
        const itemsHtml = addedItems.map((item) => {
          const imgSrc = item.image ? this._escHtml(item.image) : '';
          const img = imgSrc
            ? `<img src="${imgSrc}" alt="" class="cls-modal__item-img">`
            : `<div class="cls-modal__item-img cls-modal__item-img--empty"></div>`;
          const props = Object.entries(item.properties || {})
            .filter(([k]) => !k.startsWith('_'))
            .map(([k, v]) => `<span class="cls-modal__prop">${this._escHtml(k)}: ${this._escHtml(v)}</span>`)
            .join('');
          // Price: use whatever Shopify gives us
          const price = this._formatMoney(item);
          const itemKey = this._escHtml(item.key || '');
          return `
            <div class="cls-modal__item" data-item-key="${itemKey}">
              ${img}
              <div class="cls-modal__item-info">
                <p class="cls-modal__item-title">${this._escHtml(item.product_title)}</p>
                <p class="cls-modal__item-variant">${item.variant_title && item.variant_title !== 'Default Title' ? this._escHtml(item.variant_title) : ''}</p>
                <div class="cls-modal__props">${props}</div>
              </div>
              <div class="cls-modal__item-right">
                <p class="cls-modal__item-price">${price}</p>
                <div class="cls-modal__qty">
                  <button class="cls-modal__qty-btn cls-modal__qty-minus" data-key="${itemKey}" data-delta="-1">−</button>
                  <span class="cls-modal__qty-val" data-key="${itemKey}">${item.quantity}</span>
                  <button class="cls-modal__qty-btn cls-modal__qty-plus" data-key="${itemKey}" data-delta="1">+</button>
                </div>
                <button class="cls-modal__remove" data-key="${itemKey}" title="Remove">&#10005;</button>
              </div>
            </div>
          `;
        }).join('');

        // Remove existing modal if any
        document.getElementById('cls-cart-modal')?.remove();

        const modal = document.createElement('div');
        modal.id = 'cls-cart-modal';
        modal.className = 'cls-modal';
        modal.innerHTML = `
          <div class="cls-modal__overlay"></div>
          <div class="cls-modal__dialog">
            <div class="cls-modal__header">
              <span class="cls-modal__check">&#10003;</span>
              <h3 class="cls-modal__title">${this.translations.added || 'Added to Cart!'}</h3>
              <button class="cls-modal__close" aria-label="Close">&times;</button>
            </div>
            <div class="cls-modal__items">${itemsHtml}</div>
            <div class="cls-modal__actions">
              <button class="cls-modal__btn cls-modal__btn--continue">${this.translations.continue_shopping || 'Continue Shopping'}</button>
              <a href="${window.routes.cart_url}" class="cls-modal__btn cls-modal__btn--cart">${this.translations.view_cart || 'View Cart'}</a>
              <a href="/checkout" class="cls-modal__btn cls-modal__btn--checkout">${this.translations.checkout || 'Checkout'}</a>
            </div>
          </div>
        `;

        document.body.appendChild(modal);

        // Bind close events
        modal.querySelector('.cls-modal__close').addEventListener('click', () => modal.remove());
        modal.querySelector('.cls-modal__overlay').addEventListener('click', () => modal.remove());
        modal.querySelector('.cls-modal__btn--continue').addEventListener('click', () => modal.remove());

        // Bind quantity buttons
        modal.querySelectorAll('.cls-modal__qty-btn').forEach((btn) => {
          btn.addEventListener('click', () => {
            const key = btn.dataset.key;
            const delta = parseInt(btn.dataset.delta, 10);
            const qtyEl = modal.querySelector(`.cls-modal__qty-val[data-key="${key}"]`);
            if (!key || !qtyEl) return;

            const currentQty = parseInt(qtyEl.textContent, 10) || 1;
            const newQty = Math.max(0, currentQty + delta);

            if (newQty === 0) {
              // Remove item
              btn.closest('.cls-modal__item')?.querySelector('.cls-modal__remove')?.click();
              return;
            }

            qtyEl.textContent = newQty;
            btn.disabled = true;

            fetch(window.routes.cart_change_url || '/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: key, quantity: newQty }),
            })
              .then((r) => r.json())
              .then((cart) => {
                btn.disabled = false;
                // Update badge
                document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach((el) => {
                  el.textContent = cart.item_count;
                });
                // Update price in modal — find the line item in cart response
                const lineItem = cart.items?.find((i) => i.key === key);
                if (lineItem) {
                  const priceEl = btn.closest('.cls-modal__item')?.querySelector('.cls-modal__item-price');
                  if (priceEl) priceEl.textContent = this._formatMoney(lineItem);
                }
              })
              .catch(() => { btn.disabled = false; });
          });
        });

        // Bind remove buttons
        modal.querySelectorAll('.cls-modal__remove').forEach((btn) => {
          btn.addEventListener('click', () => {
            const key = btn.dataset.key;
            if (!key) return;
            btn.disabled = true;
            btn.textContent = '...';
            fetch(window.routes.cart_change_url || '/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 }),
            })
              .then((r) => r.json())
              .then((cart) => {
                // Remove the item row
                const row = btn.closest('.cls-modal__item');
                if (row) row.remove();
                // Update cart badge
                document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach((el) => {
                  el.textContent = cart.item_count;
                });
                // If no items left, close modal
                if (!modal.querySelector('.cls-modal__item')) {
                  modal.remove();
                }
              })
              .catch(() => { btn.disabled = false; btn.textContent = '✕'; });
          });
        });
        document.addEventListener('keydown', function onEsc(e) {
          if (e.key === 'Escape') { modal.remove(); document.removeEventListener('keydown', onEsc); }
        });

        // Animate in
        requestAnimationFrame(() => modal.classList.add('cls-modal--open'));
      }

      buildCartItems() {
        const items = [];
        const colorProp = this.isColorCL && this.selectedColor ? { Color: this.selectedColor } : {};

        if (this.mode === 'no-power') {
          const packQty = this.packSelect ? parseInt(this.packSelect.value, 10) || 1 : 1;

          if (this.isColorCL) {
            // Color CL no-power: use the parent product's color variant
            if (!this.selectedColorVariantId) {
              this.showError(this.translations.error || 'Please select a color.');
              return null;
            }
            const colorData = this.colors.find((c) => c.variantId === this.selectedColorVariantId);
            const colorStock = this.getAvailableStock(colorData);
            if (packQty > colorStock) {
              this.showError(this.formatStockError(this.selectedColor || '', colorStock, packQty));
              return null;
            }
            items.push({
              id: this.selectedColorVariantId,
              quantity: packQty,
              properties: { ...colorProp, Eye: 'No Power', Boxes: String(packQty) },
            });
            return items;
          }

          // Regular CL no-power
          const planoVariant = this.findVariant(this.planoValue);
          if (!planoVariant) {
            this.showError(this.translations.planoUnavailable || 'The plano (no power) variant is not available.');
            return null;
          }
          const planoStock = this.getAvailableStock(planoVariant);
          if (packQty > planoStock) {
            this.showError(this.formatStockError(this.planoValue, planoStock, packQty));
            return null;
          }
          items.push({
            id: planoVariant.id,
            quantity: packQty,
            properties: { Eye: 'No Power', Boxes: String(packQty) },
          });
          return items;
        }

        // With-power mode
        const isSameRx = this.sameRxCheckbox && this.sameRxCheckbox.checked;
        const packQty = this.packSelect ? parseInt(this.packSelect.value, 10) || 1 : 1;

        const leftPower = this.sphereLeft ? this.sphereLeft.value : '';
        const rightPower = isSameRx
          ? leftPower
          : this.sphereRight
            ? this.sphereRight.value
            : '';

        if (!leftPower) {
          this.showError(this.translations.selectLeftPower || 'Please select a power for the Left Eye.');
          return null;
        }
        if (!rightPower) {
          this.showError(this.translations.selectRightPower || 'Please select a power for the Right Eye.');
          return null;
        }

        const leftVariant = this.findVariant(leftPower);
        const rightVariant = this.findVariant(rightPower);

        if (!leftVariant || !leftVariant.available) {
          this.showError(this.translations.leftUnavailable || 'The selected Left Eye power is unavailable.');
          return null;
        }
        if (!rightVariant || !rightVariant.available) {
          this.showError(this.translations.rightUnavailable || 'The selected Right Eye power is unavailable.');
          return null;
        }

        if (isSameRx) {
          // Same Rx: both eyes same power — use Pack Selection as total quantity
          const stock = this.getAvailableStock(leftVariant);
          if (packQty > stock) {
            this.showError(this.formatStockError(leftPower, stock, packQty));
            return null;
          }
          items.push({
            id: leftVariant.id,
            quantity: packQty,
            properties: {
              ...colorProp,
              Power: leftPower,
              Eye: 'Both Eyes (Same Rx)',
              Boxes: String(packQty),
            },
          });
        } else {
          // Different Rx: use per-eye box quantities
          const leftQty = this.boxesLeft ? parseInt(this.boxesLeft.value, 10) || 1 : 1;
          const rightQty = this.boxesRight ? parseInt(this.boxesRight.value, 10) || 1 : 1;

          const leftStock = this.getAvailableStock(leftVariant);
          if (leftQty > leftStock) {
            this.showError(this.formatStockError(leftPower, leftStock, leftQty));
            return null;
          }
          const rightStock = this.getAvailableStock(rightVariant);
          if (rightQty > rightStock) {
            this.showError(this.formatStockError(rightPower, rightStock, rightQty));
            return null;
          }
          items.push({
            id: leftVariant.id,
            quantity: leftQty,
            properties: { ...colorProp, Eye: 'Left (OS)', Power: leftPower },
          });
          items.push({
            id: rightVariant.id,
            quantity: rightQty,
            properties: { ...colorProp, Eye: 'Right (OD)', Power: rightPower },
          });
        }

        return items;
      }

      formatStockError(power, stock, requested) {
        const template = this.translations.insufficientStock ||
          'Only [stock] available for power [power]. You requested [requested].';
        return template
          .replace('[stock]', stock)
          .replace('[power]', power)
          .replace('[requested]', requested);
      }

      _updateCartBadge() {
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            const count = cart.item_count || 0;
            if (!count) return;

            let bubbles = document.querySelectorAll('.cart-count-bubble');

            // Dawn only renders .cart-count-bubble when cart is non-empty.
            // If cart was empty on page load, the element doesn't exist — create it.
            if (!bubbles.length) {
              document.querySelectorAll('#cart-icon-bubble, #cart-icon-bubble-mobile').forEach((icon) => {
                const bubble = document.createElement('div');
                bubble.className = 'cart-count-bubble';
                const span = document.createElement('span');
                span.setAttribute('aria-hidden', 'true');
                bubble.appendChild(span);
                icon.appendChild(bubble);
              });
              bubbles = document.querySelectorAll('.cart-count-bubble');
            }

            bubbles.forEach((bubble) => {
              let span = bubble.querySelector('span[aria-hidden="true"]');
              if (!span) {
                span = document.createElement('span');
                span.setAttribute('aria-hidden', 'true');
                bubble.prepend(span);
              }
              span.textContent = count;
            });
          })
          .catch(() => {});
      }

      _escHtml(str) {
        const d = document.createElement('div');
        d.textContent = str;
        return d.innerHTML;
      }

      _formatMoney(item) {
        // For BHD shops, Shopify cart endpoints return prices in unpredictable
        // formats. Source of truth = the DOM price element (Liquid rendered it
        // correctly via shop.money_format).
        const unitPrice = this._readUnitPriceFromDOM();
        if (unitPrice != null) {
          const qty = Number(item.quantity || 1);
          return (unitPrice * qty).toFixed(3) + ' BHD';
        }

        // Fallback: original cents-based logic (works for non-BHD shops)
        let subunits = 0;
        for (const key of ['final_line_price', 'line_price', 'price', 'original_line_price']) {
          if (item[key] != null && !isNaN(Number(item[key]))) {
            subunits = Number(item[key]);
            break;
          }
        }
        if (!Number.isInteger(subunits) && subunits > 0) {
          subunits = Math.round(subunits * 100);
        }
        return this._formatCents(subunits);
      }

      _readUnitPriceFromDOM() {
        // Read the canonical product price block (kept in sync by updatePrice()).
        const selectors = [
          '.price__sale .price-item--sale.price-item--last',
          '.price__sale .price-item--sale',
          '.price__regular .price-item--regular',
          '.price .price-item--sale.price-item--last',
          '.price .price-item--regular',
        ];
        for (const sel of selectors) {
          const el = document.querySelector(sel);
          const v = this._parsePriceText(el && el.textContent);
          if (v != null) return v;
        }
        return null;
      }

      _parsePriceText(str) {
        if (!str) return null;
        const m = str.match(/(\d+(?:[.,]\d+)?)/);
        if (!m) return null;
        return parseFloat(m[1].replace(',', '.'));
      }

      _formatCents(subunits) {
        // Use Shopify's built-in formatter if available (handles all currencies correctly)
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          const fmt =
            this.getAttribute('data-money-format') ||
            (window.theme && window.theme.moneyFormat) ||
            '';
          return fmt ? Shopify.formatMoney(subunits, fmt) : Shopify.formatMoney(subunits);
        }

        // Fallback: manual formatting (divides by 100 — standard Shopify subunit)
        const amount = (subunits / 100).toFixed(2);
        const amountNoDecimals = Math.round(subunits / 100).toString();
        const amountWithComma = amount.replace('.', ',');
        const amountWithApostrophe = amount.replace('.', "'");

        const fmt =
          this.getAttribute('data-money-format') ||
          (window.theme && window.theme.moneyFormat) ||
          '{{amount}}';

        return fmt
          .replace('{{amount_with_apostrophe_separator}}', amountWithApostrophe)
          .replace('{{amount_with_comma_separator}}', amountWithComma)
          .replace('{{amount_no_decimals}}', amountNoDecimals)
          .replace('{{amount}}', amount);
      }

      showSuccess() {
        if (this.btnText) this.btnText.textContent = this.translations.added || 'Added!';
        if (this.addBtn) this.addBtn.disabled = true;
        setTimeout(() => this.updateButtonState(), 1500);
      }

      /* ── Sold-out marking ── */

      markSoldOutOptions() {
        [this.sphereLeft, this.sphereRight].forEach((select) => {
          if (!select) return;
          select.querySelectorAll('option').forEach((opt) => {
            if (!opt.value) return;
            const v = this.findVariant(opt.value);
            if (v && !v.available) {
              // Hide sold-out powers entirely
              opt.hidden = true;
              opt.disabled = true;
            } else if (v) {
              opt.hidden = false;
              opt.disabled = false;
              opt.textContent = opt.value;
            }
          });
        });
      }
    }
  );
}
